/**
 * Shared typography presets + helpers used by:
 *   - components/TypographyPanel.jsx  (admin editor UI)
 *   - context/SettingsContext.jsx     (runtime CSS variable injection)
 *
 * Adding a font is two steps: add a row here AND add the matching family
 * to the GOOGLE_FONTS_HREF in SettingsContext so it loads at runtime.
 */

export const TYPOGRAPHY_FONT_PRESETS = {
  system: {
    label: 'System',
    stack: "'Segoe UI Variable', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
    google: null,
  },
  inter: {
    label: 'Inter',
    stack: "'Inter', system-ui, sans-serif",
    google: 'Inter:wght@400;500;600;700;800',
  },
  poppins: {
    label: 'Poppins',
    stack: "'Poppins', 'Helvetica Neue', Arial, sans-serif",
    google: 'Poppins:wght@400;500;600;700;800',
  },
  roboto: {
    label: 'Roboto',
    stack: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
    google: 'Roboto:wght@400;500;700;900',
  },
  lora: {
    label: 'Lora',
    stack: "'Lora', Georgia, 'Times New Roman', serif",
    google: 'Lora:wght@400;500;600;700',
  },
  playfair: {
    label: 'Playfair',
    stack: "'Playfair Display', Georgia, serif",
    google: 'Playfair+Display:wght@400;500;700;800;900',
  },
  montserrat: {
    label: 'Montserrat',
    stack: "'Montserrat', 'Helvetica Neue', Arial, sans-serif",
    google: 'Montserrat:wght@400;500;600;700;800',
  },
};

// Per-element size / weight / italic defaults. Each lives under a
// device key so desktop and mobile can be tuned independently. The
// mobile default falls back to the same baseline as desktop — admins
// override only the breakpoints they care about.
const _flatNullSizes = {
  h1: null, h2: null, h3: null, body: null, small: null, button: null,
  product_name: null, product_size: null, product_min_qty: null,
  product_price: null, product_uom: null, category_tile: null,
};
const _flatNullWeights = { ...Object.keys(_flatNullSizes).reduce((a,k)=>(a[k]=null,a),{}) };
const _flatFalseItalics = { ...Object.keys(_flatNullSizes).reduce((a,k)=>(a[k]=false,a),{}) };

export const TYPOGRAPHY_DEFAULTS = {
  body_font: 'system',
  heading_font: 'system',
  font_scale: 1,
  letter_spacing: 0,
  apply_to: ['storefront', 'driver'],
  sizes:   { desktop: { ..._flatNullSizes },   mobile: { ..._flatNullSizes } },
  weights: { desktop: { ..._flatNullWeights }, mobile: { ..._flatNullWeights } },
  italics: { desktop: { ..._flatFalseItalics }, mobile: { ..._flatFalseItalics } },
};

// All recognised device buckets — used by the UI tab + the runtime
// applier. Order matters: the first entry is the visual default in
// the editor.
export const TYPOGRAPHY_DEVICES = ['desktop', 'mobile'];

// The breakpoint at which mobile rules give way to desktop rules.
// Matches Tailwind's `sm:` (640px). The storefront CSS in
// `index.css` uses this same value in its @media query — keep them
// in sync.
export const TYPOGRAPHY_MOBILE_MAX_PX = 639;

// Base size of each element in rem when font_scale = 1. These mirror
// the actual storefront card so the Live Preview matches reality.
export const TYPOGRAPHY_BASE_SIZES = {
  h1: 2.5,    // hero headline
  h2: 1.5,    // section title
  h3: 1.125,  // card title
  body: 1,    // paragraph
  small: 0.75,// label / caption
  button: 0.875, // CTA text
  // Product card defaults (Category grid).
  product_name:    0.9375, // 15px
  product_size:    0.75,   // 12px ("1X24X50 ML")
  product_min_qty: 0.625,  // 10px (MIN 1 CTN)
  product_price:   1,      // 16px (OMR 1.008)
  product_uom:     0.75,   // 12px (/ ctn)
  // Home-page category tile title.
  category_tile:   1.5,    // 24px (text-lg → text-2xl responsive)
};

// Min / max + step per field for the slider UI.
export const TYPOGRAPHY_SIZE_BOUNDS = {
  h1:     { min: 1.5, max: 5.0, step: 0.05 },
  h2:     { min: 1.0, max: 3.5, step: 0.05 },
  h3:     { min: 0.875, max: 2.5, step: 0.05 },
  body:   { min: 0.75, max: 1.5, step: 0.0625 },
  small:  { min: 0.625, max: 1.0, step: 0.0625 },
  button: { min: 0.75, max: 1.25, step: 0.0625 },
  product_name:    { min: 0.75,  max: 1.75, step: 0.0625 },
  product_size:    { min: 0.625, max: 1.25, step: 0.0625 },
  product_min_qty: { min: 0.5,   max: 1.0,  step: 0.0625 },
  product_price:   { min: 0.875, max: 2.0,  step: 0.0625 },
  product_uom:     { min: 0.625, max: 1.25, step: 0.0625 },
  category_tile:   { min: 1.0,   max: 3.0,  step: 0.0625 },
};

// Allowed font-weight CSS values exposed in the UI.
export const TYPOGRAPHY_WEIGHT_OPTIONS = [
  { value: null,     label: 'Auto'     },
  { value: '400',    label: 'Regular'  },
  { value: '500',    label: 'Medium'   },
  { value: '600',    label: 'Semibold' },
  { value: '700',    label: 'Bold'     },
  { value: '800',    label: 'Extra'    },
];

/** Effective size in rem for the given field on the given device.
 * Falls back to the OTHER device's override before the auto scale —
 * this means an admin who only sets a desktop size for H1 still gets
 * that override on mobile until they explicitly differentiate. */
export const resolveTypographySize = (cfg, field, device = 'desktop') => {
  const desktopOverride = cfg?.sizes?.desktop?.[field];
  const mobileOverride  = cfg?.sizes?.mobile?.[field];
  const order = device === 'mobile' ? [mobileOverride, desktopOverride] : [desktopOverride, mobileOverride];
  for (const v of order) {
    if (typeof v === 'number' && v > 0) return v;
  }
  return (TYPOGRAPHY_BASE_SIZES[field] || 1) * (cfg?.font_scale ?? 1);
};

/** Effective weight for the given field on the given device. Returns
 * `null` when neither device has an explicit value (caller should
 * leave the element's natural weight intact). */
export const resolveTypographyWeight = (cfg, field, device = 'desktop') => {
  const d = cfg?.weights?.desktop?.[field];
  const m = cfg?.weights?.mobile?.[field];
  if (device === 'mobile') return m ?? d ?? null;
  return d ?? m ?? null;
};

/** Effective italic flag for the given field on the given device.
 * Mobile inherits desktop only when desktop explicitly sets italic
 * AND mobile has not been touched (mobile flag must literally === true). */
export const resolveTypographyItalic = (cfg, field, device = 'desktop') => {
  const target = cfg?.italics?.[device]?.[field];
  if (typeof target === 'boolean') return target;
  return !!cfg?.italics?.desktop?.[field];
};

/** Backwards-compat helper: an older config stored `sizes: { h1: 2 }`
 * (flat). The new shape is `sizes: { desktop: { h1: 2 }, mobile: { h1: 2 } }`.
 * Detect either and return the nested form. */
const _migrateOrMerge = (raw, def) => {
  if (!raw || typeof raw !== 'object') return { desktop: { ...def.desktop }, mobile: { ...def.mobile } };
  if (!('desktop' in raw) && !('mobile' in raw)) {
    // Legacy flat shape — wrap under both devices so the value stays
    // visible (and the admin can then differentiate mobile later).
    return {
      desktop: { ...def.desktop, ...raw },
      mobile:  { ...def.mobile,  ...raw },
    };
  }
  return {
    desktop: { ...def.desktop, ...(raw.desktop || {}) },
    mobile:  { ...def.mobile,  ...(raw.mobile  || {}) },
  };
};

/** Merge any persisted typography sub-doc on top of defaults so unset
 * fields don't trip the UI / runtime injector. */
export const resolveTypographyConfig = (raw) => {
  const r = raw || {};
  return {
    body_font: r.body_font || TYPOGRAPHY_DEFAULTS.body_font,
    heading_font: r.heading_font || TYPOGRAPHY_DEFAULTS.heading_font,
    font_scale: typeof r.font_scale === 'number' ? r.font_scale : TYPOGRAPHY_DEFAULTS.font_scale,
    letter_spacing: typeof r.letter_spacing === 'number' ? r.letter_spacing : TYPOGRAPHY_DEFAULTS.letter_spacing,
    apply_to: Array.isArray(r.apply_to) ? r.apply_to : TYPOGRAPHY_DEFAULTS.apply_to,
    sizes:   _migrateOrMerge(r.sizes,   TYPOGRAPHY_DEFAULTS.sizes),
    weights: _migrateOrMerge(r.weights, TYPOGRAPHY_DEFAULTS.weights),
    italics: _migrateOrMerge(r.italics, TYPOGRAPHY_DEFAULTS.italics),
  };
};
